<script setup lang="ts">
import type { NavigationBarMenuItem } from '../navigation-bar';
import HugeIcon from '../../HugeIcon/HugeIcon.vue';
import ColorTag from '../../ColorTag/ColorTag.vue';
import TTooltip from '../../TTooltip.vue';

const props = defineProps<{
  item: NavigationBarMenuItem;
  compact: boolean;
  isActive: boolean;
}>();

const emits = defineEmits<{
  navigateTo: [href: string, openInNewTab: boolean];
}>();

const NAVIGATION_ICON_SIZE = 20;

const colorTagText = props.item.label
  .toUpperCase()
  .trim()
  .replaceAll(' ', '')
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  .substring(0, 3);
</script>

<template>
  <TTooltip
    :content="props.compact ? props.item.label : null"
    placement="right"
    class="flex w-full items-center justify-center"
  >
    <div class="flex w-full items-center justify-center">
      <button
        class="flex cursor-pointer items-center justify-center rounded-md p-2 font-medium text-slate-700 transition-colors hover:bg-white hover:shadow-sm"
        :class="[
          props.compact ? 'h-10 min-h-10 w-10 min-w-10' : 'min-w-0 flex-1',
          props.isActive && 'bg-white shadow-sm',
        ]"
        @click="
          () => {
            if (props.item.onClick) {
              props.item.onClick();
              return;
            }
            if (props.item.href) {
              emits('navigateTo', props.item.href, !!props.item.openInNewTab);
            }
          }
        "
      >
        <div class="flex items-center justify-center">
          <ColorTag
            v-if="props.item.color"
            :color="props.item.color"
            :background-color="props.item.backgroundColor ?? 'black'"
            :class="[props.compact ? 'mr-0' : 'mr-3']"
            :text="props.compact ? colorTagText : undefined"
          />
          <img
            v-else-if="props.item.image"
            :src="props.item.image"
            :class="[
              'rounded-sm',
              props.compact ? 'mr-0 w-12' : 'mr-2 h-6 w-6',
            ]"
          />
          <HugeIcon
            v-else-if="props.item.icon"
            :icon="props.item.icon"
            :class="[props.compact ? 'mr-0' : 'mr-2']"
            :size="NAVIGATION_ICON_SIZE"
          />
        </div>
        <div
          class="truncate overflow-hidden text-left transition-all duration-200 ease-out"
          :class="[props.compact ? 'h-0 w-0 opacity-0' : 'w-full opacity-100']"
        >
          {{ props.item.label }}
        </div>
      </button>

      <TTooltip
        v-if="props.item.trailingAction && !props.compact"
        :content="props.item.trailingAction.label"
        placement="right"
      >
        <button
          :aria-label="props.item.trailingAction.label"
          class="text-primary-500 hover:bg-primary-50 hover:text-primary-600 focus-visible:ring-primary-500 flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md transition-colors focus-visible:ring-2 focus-visible:outline-none"
          @click="props.item.trailingAction.onClick"
        >
          <HugeIcon
            :icon="props.item.trailingAction.icon"
            :size="NAVIGATION_ICON_SIZE"
          />
        </button>
      </TTooltip>
    </div>
  </TTooltip>
</template>
